<template>
  <div class="tcc-plan__wrapper">
    <div class="tcc-plan__wrapper--contents">
      <div class="tcc-plan__wrapper--title" v-html="heading"></div>
      <p class="tcc-plan__wrapper--subtitle" v-html="subtitle"></p>
    </div>
    <div class="tcc-plan__wrapper--container">
      <template v-if="plans && plans.length">
        <tabs v-on:tab-selected="tabSelected($event)">
          <tab v-for="(plan, index) in plans"
               :key="index"
               v-if="plan.variations && plan.variations.length"
               v-bind:hash="false"
               v-bind:name="plan.name"
               v-bind:selected="planSelected(plan)"
               v-bind:alias="plan.name">
            <div class="tcc-plan__box">
              <div class="tcc-plan__box--details">
                <p class="tcc-plan__box--details__starting-at" v-if="planTemporaryPrice(plan)">Starting at</p>
                <div class="tcc-plan__box--details__price">
                  <template v-if="planPrice(plan)">
                    <span>£</span>{{ planPrice(plan) }}<sup v-if="vatDisplay"> + VAT</sup> / month
                    <div class="tcc-plan__box--details__price--payment-period"
                         v-html="planPaymentPeriod(plan)"></div>
                  </template>
                </div>
                <div class="tcc-plan__box--details__options">
                  <div class="tcc-plan__box--details__option"
                       v-if="!preSelectedSize"
                       :class="[{ 'active': sizeSelectorActive }]">
                    <label for="sizeOptions" class="tcc-plan__box--details__option--label dynamic-option">Select organisation size</label>
                    <div class="tcc-plan__box--details__option--value">
                      <app-select class="tcc-plan__box--details__option--selector"
                                  customType="planSelector"
                                  :searchable="false"
                                  :customActive="false"
                                  :clearable="false"
                                  :placeholder="'Confirm number of employees'"
                                  :options="plan.sizeOptions || []"
                                  v-bind:value="plan.selectedPlanSize"
                                  @change="planSizeChanged($event, plan)"
                                  data-vv-name="Size"
                                  data-lpignore="true"
                                  name="sizeOptions"
                                  id="sizeOptions" />
                    </div>
                  </div>
                  <div class="tcc-plan__box--details__option"
                       :class="[{ 'active': billingSelectorActive, 'disabled': !plan.selectedPlanSize }]">
                    <label for="billingOptions" class="tcc-plan__box--details__option--label dynamic-option">Select billing cycle</label>
                    <div class="tcc-plan__box--details__option--value">
                      <app-select class="tcc-plan__box--details__option--selector"
                                  customType="planSelector"
                                  :searchable="false"
                                  :customActive="false"
                                  :clearable="false"
                                  :placeholder="!plan.selectedPlanSize ? 'Select organisation size first' : 'Choose an option'"
                                  :disabled="!plan.selectedPlanSize"
                                  :options="plan.billingOptions || []"
                                  v-bind:value="plan.selectedPlanBilling"
                                  v-on:search:focus="searchFocus(plan, 'billing')"
                                  v-on:search:blur="searchBlur(plan, 'billing')"
                                  @change="planBillingChanged($event, plan)"
                                  data-vv-name="Billing"
                                  data-lpignore="true"
                                  name="billingOptions"
                                  id="billingOptions" />
                    </div>
                  </div>
                  <div class="tcc-plan__box--details__option"
                       :class="[{ 'active': commitmentSelectorActive, 'disabled': !plan.selectedPlanSize }]">
                    <label for="commitmentOptions" class="tcc-plan__box--details__option--label dynamic-option">Select commitment period</label>
                    <div class="tcc-plan__box--details__option--value">
                      <app-select class="tcc-plan__box--details__option--selector"
                                  customType="planSelector"
                                  :searchable="false"
                                  :customActive="false"
                                  :clearable="false"
                                  :placeholder="!plan.selectedPlanSize ? 'Select organisation size first' : 'Choose an option'"
                                  :disabled="!plan.selectedPlanSize"
                                  :options="plan.commitmentOptions || []"
                                  v-bind:value="plan.selectedPlanCommitment"
                                  v-on:search:focus="searchFocus(plan, 'commitment')"
                                  v-on:search:blur="searchBlur(plan, 'commitment')"
                                  @change="planCommitmentChanged($event, plan)"
                                  data-vv-name="Commitment"
                                  data-lpignore="true"
                                  name="commitmentOptions"
                                  id="commitmentOptions" />
                    </div>
                  </div>
                </div>
                <div class="tcc-plan__box--details__actions hide-on-mobile">
                  <button v-on:click="selectPlan()" :class="[{submitting: submitting}]">
                      <span>
                        <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="check" class="svg-inline--fa fa-check fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                          <path fill="currentColor" d="M480 128c0 8.188-3.125 16.38-9.375 22.62l-256 256C208.4 412.9 200.2 416 192 416s-16.38-3.125-22.62-9.375l-128-128C35.13 272.4 32 264.2 32 256c0-18.28 14.95-32 32-32c8.188 0 16.38 3.125 22.62 9.375L192 338.8l233.4-233.4C431.6 99.13 439.8 96 448 96C465.1 96 480 109.7 480 128z"></path>
                        </svg>
                      </span>
                    Choose this plan
                  </button>
                </div>
              </div>
              <div class="tcc-plan__box--features" :class="[{ 'voucher-shown': voucherAreaActive }]">
                <div class="tcc-plan__box--features__voucher"
                     v-bind:id="'voucherContainer-' + index"
                     v-bind:aria-expanded="(voucherAreaActive || voucherAreaTransitioning) || 'false'"
                     :class="[{ 'active': voucherAreaActive, 'transition-in': voucherAreaTransitioning }]">
                  <button class="tcc-plan__box--features__voucher--trigger"
                          :class="[{ 'activate': !voucherAreaActive && !voucherAreaTransitioning, 'deactivate': voucherAreaActive || voucherAreaTransitioning }]"
                          v-bind:aria-label="!voucherAreaActive && !voucherAreaTransitioning ? 'Use a voucher' : 'Close voucher area'"
                          v-bind:aria-expanded="(voucherAreaActive || voucherAreaTransitioning) || 'false'"
                          v-bind:aria-controls="'voucherContainer-' + index"
                          v-on:click.prevent.stop="activateVoucherArea()">
                    <template v-if="!voucherAreaActive && !voucherAreaTransitioning">
                      <template v-if="voucher">
                        Voucher applied: {{ voucher }}
                        <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="pen" class="svg-inline--fa fa-pen" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M497.9 74.16L437.8 14.06c-18.75-18.75-49.19-18.75-67.93 0l-56.53 56.55l127.1 128l56.56-56.55C516.7 123.3 516.7 92.91 497.9 74.16zM290.8 93.23l-259.7 259.7c-2.234 2.234-3.755 5.078-4.376 8.176l-26.34 131.7C-1.921 504 7.95 513.9 19.15 511.7l131.7-26.34c3.098-.6191 5.941-2.141 8.175-4.373l259.7-259.7L290.8 93.23z"></path></svg>
                      </template>
                      <template v-else>
                        Use a voucher
                        <svg aria-hidden="true" focusable="false" data-prefix="far" data-icon="circle-question" class="svg-inline--fa fa-circle-question" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM256 464c-114.7 0-208-93.31-208-208S141.3 48 256 48s208 93.31 208 208S370.7 464 256 464zM256 336c-18 0-32 14-32 32s13.1 32 32 32c17.1 0 32-14 32-32S273.1 336 256 336zM289.1 128h-51.1C199 128 168 159 168 198c0 13 11 24 24 24s24-11 24-24C216 186 225.1 176 237.1 176h51.1C301.1 176 312 186 312 198c0 8-4 14.1-11 18.1L244 251C236 256 232 264 232 272V288c0 13 11 24 24 24S280 301 280 288V286l45.1-28c21-13 34-36 34-60C360 159 329 128 289.1 128z"></path></svg>
                      </template>
                    </template>
                    <template v-else>
                      <svg aria-hidden="true" focusable="false" data-prefix="far" data-icon="xmark" class="svg-inline--fa fa-xmark" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path fill="currentColor" d="M312.1 375c9.369 9.369 9.369 24.57 0 33.94s-24.57 9.369-33.94 0L160 289.9l-119 119c-9.369 9.369-24.57 9.369-33.94 0s-9.369-24.57 0-33.94L126.1 256L7.027 136.1c-9.369-9.369-9.369-24.57 0-33.94s24.57-9.369 33.94 0L160 222.1l119-119c9.369-9.369 24.57-9.369 33.94 0s9.369 24.57 0 33.94L193.9 256L312.1 375z"></path></svg>
                    </template>
                  </button>
                  <form class="tcc-plan__box--features__voucher--form"
                        v-if="voucherAreaActive"
                        v-bind:aria-label="'voucher-' + index"
                        v-on:submit.prevent.stop="submitVoucher()">
                    <template v-if="!hideVoucherSubmitForm">
                      <div :id="'voucher-' + index" class="tcc-plan__box--features__voucher--title">Do you have a voucher? Enter the code below.</div>
                      <div class="tcc-plan__box--features__voucher--form__group"
                           :class="[{ 'not-empty': !!voucher }]">
                        <input type="text"
                               placeholder="Type in here..."
                               name="voucher"
                               v-model="voucher" />
                        <button type="submit">Submit voucher</button>
                      </div>
                    </template>
                    <div class="tcc-plan__box--features__voucher--form__status"
                         :class="[{ 'saved-voucher': hideVoucherSubmitForm }]">
                      <template v-if="voucherSubmitted">
                        <template v-if="invalidVoucher">
                          <div class="tcc-plan__box--features__voucher--form__status--title">
                            <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="circle-xmark" class="svg-inline--fa fa-circle-xmark" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                              <path fill="currentColor" d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256C397.4 512 512 397.4 512 256S397.4 0 256 0zM336.1 303c9.375 9.375 9.375 24.56 0 33.94c-9.381 9.381-24.56 9.373-33.94 0L256 289.9l-47.03 47.03c-9.381 9.381-24.56 9.373-33.94 0c-9.375-9.375-9.375-24.56 0-33.94l47.03-47.03L175 208.1c-9.375-9.375-9.375-24.56 0-33.94s24.56-9.375 33.94 0L256 222.1l47.03-47.03c9.375-9.375 24.56-9.375 33.94 0s9.375 24.56 0 33.94l-47.03 47.03L336.1 303z"></path>
                            </svg>
                            The code you entered is invalid.
                          </div>
                          <div class="tcc-plan__box--features__voucher--form__status--text">
                            Please try again. If it still doesn't work, please get in touch with the code provider and check if it's correct.
                          </div>
                        </template>
                        <template v-else>
                          <div class="tcc-plan__box--features__voucher--form__status--title">
                            <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="circle-check" class="svg-inline--fa fa-circle-check" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                              <path fill="currentColor" d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM371.8 211.8l-128 128C238.3 345.3 231.2 348 224 348s-14.34-2.719-19.81-8.188l-64-64c-10.91-10.94-10.91-28.69 0-39.63c10.94-10.94 28.69-10.94 39.63 0L224 280.4l108.2-108.2c10.94-10.94 28.69-10.94 39.63 0C382.7 183.1 382.7 200.9 371.8 211.8z"></path>
                            </svg>
                            The code you entered '{{ voucher }}' was successfully applied.
                          </div>
                          <div class="tcc-plan__box--features__voucher--form__status--text">
<!--                            Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt.-->
                          </div>
                        </template>
                      </template>
                      <template v-else-if="hideVoucherSubmitForm">
                        <div class="tcc-plan__box--features__voucher--form__status--saved__title">
                          You have applied a voucher code '{{ voucher }}'.
                        </div>
                        <div class="tcc-plan__box--features__voucher--form__status--saved__actions">
                          <button type="button" v-on:click.prevent="submitAnotherVoucher()">Submit another voucher code</button>
                          <button type="button" v-on:click.prevent="deleteVoucher()">Delete voucher code</button>
                        </div>
                      </template>
                    </div>
                  </form>
                </div>
                <h3 class="tcc-plan__box--features__title">Plans features at a glance</h3>
                <div class="tcc-plan__box--features__list" v-html="planDescription(plan)"></div>
              </div>
              <div class="tcc-plan__box--details__actions hide-on-desktop">
                <button v-on:click="selectPlan()" :class="[{submitting: submitting}]">
                  <span>
                    <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="check" class="svg-inline--fa fa-check fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                      <path fill="currentColor" d="M480 128c0 8.188-3.125 16.38-9.375 22.62l-256 256C208.4 412.9 200.2 416 192 416s-16.38-3.125-22.62-9.375l-128-128C35.13 272.4 32 264.2 32 256c0-18.28 14.95-32 32-32c8.188 0 16.38 3.125 22.62 9.375L192 338.8l233.4-233.4C431.6 99.13 439.8 96 448 96C465.1 96 480 109.7 480 128z"></path>
                    </svg>
                  </span>
                  Choose this plan
                </button>
              </div>
            </div>
          </tab>
        </tabs>
      </template>
      <template v-else>
        <div class="loading-data">
          <div></div><div></div><div></div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
  import api from '@/api'
  import { mapGetters, mapActions } from 'vuex'
  import AppSelect from '@/components/admin/common/Select'

  export default {
    name: 'plans-list',
    model: {
      prop: 'planSelected',
      event: 'plan-selected'
    },
    props: {
      title: {
        type: String,
        required: false
      },
      subtitle: {
        type: String,
        required: false
      },
      currentPlan: {
        type: Object,
        required: false,
        default: null
      },
      currentVariation: {
        type: Object,
        required: false,
        default: null
      },
      vatDisplay: {
        type: Boolean,
        required: false,
        default: false
      },
      submitting: {
        type: Boolean,
        required: false,
        default: false
      },
      preSelectedSize: {
        type: Object,
        required: false,
        default: null
      },
      headingType: {
        type: String,
        required: false,
        default: 'h2'
      }
    },
    data () {
      return {
        plans: [],
        selectedPlan: null,
        voucher: null,
        voucherSubmitted: false,
        invalidVoucher: false,
        hideVoucherSubmitForm: false,
        sizeSelectorActive: false,
        billingSelectorActive: false,
        commitmentSelectorActive: false,
        voucherAreaActive: false,
        voucherAreaTransitioning: false
      }
    },
    computed: {
      ...mapGetters([
        'companySizes'
      ]),
      mutableCurrentPlan: {
        get: function () {
          return this.currentPlan
        },
        set: function (value) {
          this.$emit('current-plan:change', value)
        }
      },
      heading () {
        return `<${this.headingType}>${this.title}</${this.headingType}>`
      }
    },
    methods: {
      ...mapActions([
        'getCompanySizes'
      ]),
      findSelectedPlan () {
        let selectedPlan = null
        if (!this.plans || !this.plans.length) {
          return selectedPlan
        }
        if (this.mutableCurrentPlan) {
          selectedPlan = this.plans.find(plan => plan && plan.hasOwnProperty('variations') && plan.variations.length &&
            plan.variations.some(v => parseInt(v.minSize, 10) === parseInt(this.mutableCurrentPlan.minSize, 10) &&
              parseInt(v.paymentPeriodUnit, 10) === parseInt(this.mutableCurrentPlan.paymentPeriodUnit, 10) &&
              parseInt(v.paymentPeriodValue, 10) === parseInt(this.mutableCurrentPlan.paymentPeriodValue, 10) &&
              parseInt(v.commitment, 10) === parseInt(this.mutableCurrentPlan.commitment, 10) &&
              ((!this.mutableCurrentPlan.marketplace && !v.marketplace) || (this.mutableCurrentPlan.marketplace && v.marketplace && v.marketplace.id === this.mutableCurrentPlan.marketplace.id))))
        } else {
          // let lowerPrice
          // for (let i = 0; i < this.plans.length; i++) {
          //   if (!this.plans[i].hasOwnProperty('variations')) {
          //     continue
          //   }
          //   let lowerPriceVariation = this.plans[i]['variations'].reduce((prev, curr) => {
          //     let prevPrice = prev.price
          //     let currPrice = curr.price
          //     if (prev.paymentPeriodUnit === 0) {
          //       if (prev.paymentPeriodValue > 1) {
          //         prevPrice = parseFloat(prev.price) / parseFloat(prev.paymentPeriodValue)
          //       }
          //     } else if (prev.paymentPeriodUnit === 1) {
          //       if (prev.paymentPeriodValue > 1) {
          //         prevPrice = parseFloat(prev.price) / parseFloat(prev.paymentPeriodValue) / 12
          //       }
          //     }
          //     if (curr.paymentPeriodUnit === 0) {
          //       if (curr.paymentPeriodValue > 1) {
          //         currPrice = parseFloat(curr.price) / parseFloat(curr.paymentPeriodValue)
          //       }
          //     } else if (curr.paymentPeriodUnit === 1) {
          //       if (curr.paymentPeriodValue > 1) {
          //         currPrice = parseFloat(curr.price) / parseFloat(curr.paymentPeriodValue) / 12
          //       }
          //     }
          //     return prevPrice < currPrice ? prev.price : curr.price
          //   })
          //   if (!lowerPrice || (lowerPriceVariation && parseFloat(lowerPriceVariation) < lowerPrice)) {
          //     selectedPlan = this.plans[i]
          //   }
          //   lowerPrice = parseFloat(lowerPriceVariation)
          // }
        }
        this.selectedPlan = selectedPlan || this.plans[0]
      },
      planSelected (plan) {
        if (!this.selectedPlan) {
          return false
        }
        return this.selectedPlan.type === plan.type && this.selectedPlan.marketplace === plan.marketplace
      },
      planTemporaryPrice (plan) {
        if (!plan.selectedVariation ||
          (plan.selectedVariation.minSize === null && (!plan.hasOwnProperty('temporarySelectedVariation') || plan.temporarySelectedVariation.minSize === null)) ||
          (plan.selectedVariation.paymentPeriodUnit === null && (!plan.hasOwnProperty('temporarySelectedVariation') || plan.temporarySelectedVariation.paymentPeriodUnit === null)) ||
          (plan.selectedVariation.paymentPeriodValue === null && (!plan.hasOwnProperty('temporarySelectedVariation') || plan.temporarySelectedVariation.paymentPeriodValue === null)) ||
          (plan.selectedVariation.commitment === null && (!plan.hasOwnProperty('temporarySelectedVariation') || plan.temporarySelectedVariation.commitment === null))) {
          return true
        }
        return false
      },
      selectPlan () {
        let variation = this.planVariation(this.selectedPlan)
        this.$emit('plan-selected', {
          variation: variation
        })
      },
      planVariation (plan) {
        if (!plan.variations || !Array.isArray(plan.variations) || !plan.selectedVariation) {
          return null
        }
        return plan.variations.find(v => (v.minSize === plan.selectedVariation.minSize || (plan.hasOwnProperty('temporarySelectedVariation') && plan.temporarySelectedVariation.minSize !== null && v.minSize === plan.temporarySelectedVariation.minSize)) &&
          (v.paymentPeriodUnit === plan.selectedVariation.paymentPeriodUnit || (plan.hasOwnProperty('temporarySelectedVariation') && plan.temporarySelectedVariation.paymentPeriodUnit !== null && v.paymentPeriodUnit === plan.temporarySelectedVariation.paymentPeriodUnit)) &&
          (v.paymentPeriodValue === plan.selectedVariation.paymentPeriodValue || (plan.hasOwnProperty('temporarySelectedVariation') && plan.temporarySelectedVariation.paymentPeriodValue !== null && v.paymentPeriodValue === plan.temporarySelectedVariation.paymentPeriodValue)) &&
          (v.commitment === plan.selectedVariation.commitment || (plan.hasOwnProperty('temporarySelectedVariation') && plan.temporarySelectedVariation.commitment !== null && v.commitment === plan.temporarySelectedVariation.commitment)))
      },
      planLowerPriceVariation (plan) {
        if (!plan.variations || !Array.isArray(plan.variations) || !plan.lowerPriceVariation) {
          return null
        }
        return plan.variations.find(v => v.minSize === plan.lowerPriceVariation.minSize &&
          v.paymentPeriodUnit === plan.lowerPriceVariation.paymentPeriodUnit &&
          v.paymentPeriodValue === plan.lowerPriceVariation.paymentPeriodValue &&
          v.commitment === plan.lowerPriceVariation.commitment)
      },
      planSizeOptions (plan) {
        if (!plan.hasOwnProperty('variations') || !plan.variations.length) {
          return []
        }
        const sizes = []
        for (let i = 0; i < plan.variations.length; i++) {
          let size = this.companySizes.find(cs => cs.min === plan.variations[i].minSize)
          if (size && !sizes.find(s => s.value === size.value)) {
            sizes.push(size)
          }
        }
        return sizes.sort((a, b) => {
          return a.value > b.value ? 1 : -1
        })
      },
      selectedSizeOption (plan) {
        if (!plan.selectedVariation || plan.selectedVariation.minSize === null) {
          return null
        }
        if (this.preSelectedSize) {
          return this.preSelectedSize
        }
        let size = this.companySizes.find(cs => parseInt(cs.min) === parseInt(plan.selectedVariation.minSize))
        return size || null
      },
      planSizeChanged (event, plan) {
        if (!event) {
          plan.selectedVariation.minSize = null
          plan.selectedPlanSize = null
          this.filterOptions(plan)
          return
        }
        plan.selectedPlanSize = event
        plan.selectedVariation.minSize = event.min
        if (!plan.selectedVariation.selectedSavedVariation) {
          plan.selectedVariation.paymentPeriodUnit = null
          plan.selectedVariation.paymentPeriodValue = null
          plan.selectedVariation.commitment = null
          plan.selectedPlanBilling = null
          plan.selectedPlanCommitment = null
        }
        this.filterOptions(plan)
      },
      planBillingOptions (plan, filteredVariations) {
        if (!filteredVariations.length) {
          return []
        }
        let billings = filteredVariations.map(v => {
          let label
          let value = `${v.paymentPeriodValue}-${v.paymentPeriodUnit}`
          if (v.paymentPeriodUnit === 0) {
            if (v.paymentPeriodValue === 1) {
              label = 'Billed monthly'
            } else {
              label = `Billed every ${v.paymentPeriodValue} months`
            }
          } else {
            if (v.paymentPeriodValue === 1) {
              label = 'Billed annually'
            } else {
              label = `Billed every ${v.paymentPeriodValue} years`
            }
          }
          return {
            label: label,
            value: value
          }
        })
        return billings.reduce((acc, o) => {
          if (!acc.find(a => a.label === o.label)) {
            acc.push(o)
          }
          return acc
        }, [])
      },
      selectedBillingOption (plan) {
        if (!plan.selectedVariation || plan.selectedVariation.paymentPeriodUnit === null || plan.selectedVariation.paymentPeriodValue === null) {
          return null
        }
        let label
        let value = `${plan.selectedVariation.paymentPeriodValue}-${plan.selectedVariation.paymentPeriodUnit}`
        if (plan.selectedVariation.paymentPeriodUnit === 0) {
          if (plan.selectedVariation.paymentPeriodValue === 1) {
            label = 'Billed monthly'
          } else {
            label = `Billed every ${plan.selectedVariation.paymentPeriodValue} months`
          }
        } else {
          if (plan.selectedVariation.paymentPeriodValue === 1) {
            label = 'Billed annually'
          } else {
            label = `Billed every ${plan.selectedVariation.paymentPeriodValue} years`
          }
        }
        return {
          label: label,
          value: value
        }
      },
      planBillingChanged (event, plan) {
        if (!event) {
          plan.selectedVariation.paymentPeriodUnit = null
          plan.selectedVariation.paymentPeriodValue = null
          plan.selectedPlanBilling = null
          this.filterOptions(plan)
          return
        }
        let period = event.value.split('-')
        plan.selectedPlanBilling = event
        plan.selectedVariation.paymentPeriodUnit = parseInt(period[1], 10)
        plan.selectedVariation.paymentPeriodValue = parseInt(period[0], 10)
        let strictFilteredOptions = this.strictFilteredOptions(plan)
        let selectedCommitmentOption = this.selectedCommitmentOption(plan)
        if (!plan.selectedVariation.selectedSavedVariation) {
          if (!plan.temporarySelectedVariation && selectedCommitmentOption && strictFilteredOptions.commitment.findIndex(o => o.label === selectedCommitmentOption.label && o.value === selectedCommitmentOption.value) === -1) {
            plan.selectedVariation.commitment = null
            plan.selectedPlanCommitment = null
          }
        }
        this.filterOptions(plan)
      },
      planCommitmentOptions (plan, filteredVariations) {
        if (!filteredVariations.length) {
          return []
        }
        let commitments = filteredVariations.map(c => {
          let label
          if (c.commitment === 1) {
            label = '1 Year commitment'
          } else if (c.commitment === 3) {
            label = '3 Years commitment'
          } else {
            label = 'No commitment'
          }
          return {
            label: label,
            value: c.commitment
          }
        })
        return commitments.reduce((acc, o) => {
          if (!acc.find(a => a.label === o.label)) {
            acc.push(o)
          }
          return acc
        }, []).sort((a, b) => a.label === 'No commitment' ? -1 : a.label > b.label)
      },
      selectedCommitmentOption (plan) {
        if (!plan.selectedVariation || plan.selectedVariation.commitment === null) {
          return null
        }
        let label
        if (plan.selectedVariation.commitment === 1) {
          label = '1 Year commitment'
        } else if (plan.selectedVariation.commitment === 3) {
          label = '3 Years commitment'
        } else {
          label = 'No commitment'
        }
        return {
          label: label,
          value: plan.selectedVariation.commitment
        }
      },
      planCommitmentChanged (event, plan) {
        if (!event) {
          plan.selectedVariation.commitment = null
          plan.selectedPlanCommitment = null
          this.filterOptions(plan)
          return
        }
        plan.selectedPlanCommitment = event
        plan.selectedVariation.commitment = event.value
        let strictFilteredOptions = this.strictFilteredOptions(plan)
        let selectedBillingOption = this.selectedBillingOption(plan)
        if (!plan.selectedVariation.selectedSavedVariation) {
          if (selectedBillingOption && strictFilteredOptions.billing.findIndex(o => o.label === selectedBillingOption.label && o.value === selectedBillingOption.value) === -1) {
            plan.selectedVariation.paymentPeriodUnit = null
            plan.selectedVariation.paymentPeriodValue = null
            plan.selectedPlanBilling = null
          }
        }
        this.filterOptions(plan)
      },
      planPrice (plan) {
        let variation = this.planVariation(plan)
        if (!variation) {
          variation = this.planLowerPriceVariation(plan)
        }
        if (!variation) {
          return ''
        }
        if (variation.paymentPeriodUnit === 0) {
          if (variation.paymentPeriodValue === 1) {
            return parseFloat(variation.price)
          } else {
            return (parseFloat(variation.price) / variation.paymentPeriodValue).toFixed(2)
          }
        } else if (variation.paymentPeriodUnit === 1) {
          if (variation.paymentPeriodValue === 1) {
            return (parseFloat(variation.price) / 12).toFixed(2)
          } else {
            return (parseFloat(variation.price) / variation.paymentPeriodValue / 12).toFixed(2)
          }
        }
      },
      planDescription (plan) {
        let defaultText = '<ul><li><b>Knowledgeable enthusiasts, pragmatic expertise</b> – We understand the practical realities behind your inclusion journey and enable our clients to create policies and processes that reflect the specific needs of their workplace as well as changing cultures.</li><li><b>Focusing on D&amp;I since 2003</b> - the Clear Company has continuously enabled its clients to achieve powerful, constructive change across the employee lifecycle and beyond – influencing supply chains, inclusive customer solutions and investment in communities</li><li><b>Added Value</b> - The Clear Company provide resources and networking opportunities that focus on the latest developments in D&amp;I.&nbsp; They include regular blogs, downloadable mini guides, events and roundtables and a podcast series that provides bite-sized D&amp;I insights from business leaders<br></li></ul>'
        let variation = this.planVariation(plan)
        if (!variation) {
          return defaultText
        }
        return variation.plan && variation.plan.description && variation.plan.description !== '' ? variation.plan.description : defaultText
      },
      planPaymentPeriod (plan) {
        let variation = this.planVariation(plan)
        if (!variation || variation.paymentPeriodUnit === null || variation.paymentPeriodValue === null) {
          return ''
        }
        let text = 'Billed'
        if (variation.paymentPeriodUnit === 0) {
          if (variation.paymentPeriodValue === 1) {
            text += ' monthly'
          } else {
            text += ` every ${variation.paymentPeriodValue} months`
          }
        } else if (variation.paymentPeriodUnit === 1) {
          if (variation.paymentPeriodValue === 1) {
            text += ' yearly'
          } else {
            text += ` every ${variation.paymentPeriodValue} years`
          }
        }
        if (variation.paymentPeriodUnit === 0) {
          if (variation.paymentPeriodValue === 1) {
            text += ` at <span>£</span>${parseFloat(variation.price)}${this.vatDisplay ? '<sup> + VAT</sup>' : ''}/month`
          } else {
            text += ` at <span>£</span>${parseFloat(variation.price)}${this.vatDisplay ? '<sup> + VAT</sup>' : ''}/${variation.paymentPeriodValue} months`
          }
        } else if (variation.paymentPeriodUnit === 1) {
          if (variation.paymentPeriodValue === 1) {
            text += ` at <span>£</span>${parseFloat(variation.price)}${this.vatDisplay ? '<sup> + VAT</sup>' : ''}/year`
          } else {
            text += ` at <span>£</span>${parseFloat(variation.price)}${this.vatDisplay ? '<sup> + VAT</sup>' : ''}/${variation.paymentPeriodValue} years`
          }
        }
        return text
      },
      strictFilteredOptions (plan) {
        let result = {
          size: [],
          billing: [],
          commitment: []
        }
        if (!plan.hasOwnProperty('variations') || !Array.isArray(plan.variations) || !plan.variations.length || !plan.selectedVariation) {
          return result
        }

        let variations = plan.variations.filter(v => plan.selectedVariation.minSize === null || v.minSize === plan.selectedVariation.minSize)
          .filter(v => (plan.selectedVariation.paymentPeriodUnit === null && plan.selectedVariation.paymentPeriodValue === null) || (v.paymentPeriodUnit === plan.selectedVariation.paymentPeriodUnit && v.paymentPeriodValue === plan.selectedVariation.paymentPeriodValue))
          .filter(v => plan.selectedVariation.commitment === null || v.commitment === plan.selectedVariation.commitment)

        result.size = this.planSizeOptions(plan, variations)
        result.billing = this.planBillingOptions(plan, variations)
        result.commitment = this.planCommitmentOptions(plan, variations)

        return result
      },
      filterOptions (plan) {
        if (!plan.hasOwnProperty('variations') || !Array.isArray(plan.variations) || !plan.variations.length || !plan.selectedVariation) {
          return
        }
        let variations = plan.variations.filter(v => plan.selectedVariation.minSize === null || v.minSize === plan.selectedVariation.minSize)
        if ((plan.selectedVariation.paymentPeriodUnit === null && (!plan.hasOwnProperty('temporarySelectedVariation') || !plan.temporarySelectedVariation.hasOwnProperty('paymentPeriodUnit') || plan.temporarySelectedVariation.paymentPeriodUnit === null)) ||
          (plan.selectedVariation.paymentPeriodValue === null && (!plan.hasOwnProperty('temporarySelectedVariation') || !plan.temporarySelectedVariation.hasOwnProperty('paymentPeriodValue') || plan.temporarySelectedVariation.paymentPeriodValue === null)) ||
          (plan.selectedVariation.commitment === null && (!plan.hasOwnProperty('temporarySelectedVariation') || !plan.temporarySelectedVariation.hasOwnProperty('commitment') || plan.temporarySelectedVariation.commitment === null))) {
          variations = variations.filter(v => (plan.selectedVariation.paymentPeriodUnit === null && plan.selectedVariation.paymentPeriodValue === null) || (v.paymentPeriodUnit === plan.selectedVariation.paymentPeriodUnit && v.paymentPeriodValue === plan.selectedVariation.paymentPeriodValue))
            .filter(v => plan.selectedVariation.commitment === null || v.commitment === plan.selectedVariation.commitment)
        }

        if (!variations.length) {
          plan.variations = variations
          // this.$nextTick(() => {
          //   let planIndex = this.plans.findIndex(p => p.id === plan.id)
          //   this.plans.splice(planIndex, 1)
          // })
          // this.$emit('plans-loaded', [])
          return
        }

        // let sizes = this.planSizeOptions(plan, variations)
        let billings = this.planBillingOptions(plan, variations)
        let commitments = this.planCommitmentOptions(plan, variations)

        // this.$set(plan, 'sizeOptions', JSON.parse(JSON.stringify(sizes)))
        this.$set(plan, 'billingOptions', JSON.parse(JSON.stringify(billings)))
        this.$set(plan, 'commitmentOptions', JSON.parse(JSON.stringify(commitments)))
      },
      searchFocus (plan, type) {
        if (!plan.hasOwnProperty('temporarySelectedVariation')) {
          this.$set(plan, 'temporarySelectedVariation', {})
        }
        switch (type) {
          case 'size':
            // plan.temporarySelectedVariation.minSize = plan.selectedVariation.minSize
            // plan.selectedVariation.minSize = null
            this.sizeSelectorActive = true
            break
          case 'billing':
            plan.temporarySelectedVariation.paymentPeriodUnit = plan.selectedVariation.paymentPeriodUnit
            plan.temporarySelectedVariation.paymentPeriodValue = plan.selectedVariation.paymentPeriodValue
            plan.selectedVariation.paymentPeriodUnit = null
            plan.selectedVariation.paymentPeriodValue = null
            this.billingSelectorActive = true
            break
          case 'commitment':
            plan.temporarySelectedVariation.commitment = plan.selectedVariation.commitment
            plan.selectedVariation.commitment = null
            this.commitmentSelectorActive = true
            break
        }
        this.filterOptions(plan)
      },
      searchBlur (plan, type) {
        switch (type) {
          case 'size':
            // plan.selectedVariation.minSize = plan.temporarySelectedVariation.minSize
            this.sizeSelectorActive = false
            break
          case 'billing':
            plan.selectedVariation.paymentPeriodUnit = plan.temporarySelectedVariation.paymentPeriodUnit
            plan.selectedVariation.paymentPeriodValue = plan.temporarySelectedVariation.paymentPeriodValue
            this.billingSelectorActive = false
            break
          case 'commitment':
            plan.selectedVariation.commitment = plan.temporarySelectedVariation.commitment
            this.commitmentSelectorActive = false
            break
        }
        // this.$set(plan, 'temporarySelectedVariation', {})
        delete plan.temporarySelectedVariation
        this.$set(plan, 'selectedVariation', plan.selectedVariation)
      },
      updatePlansVariation () {
        if (!this.plans.length) {
          return
        }
        for (let i = 0; i < this.plans.length; i++) {
          if (!!this.voucher && !this.selectedPlan && this.plans[i].voucher && parseInt(this.plans[i].voucher.type, 10) === 3 && this.plans[i].marketplace && this.plans[i].marketplace.id === this.plans[i].voucher.marketplace) {
            this.selectedPlan = this.plans[i]
          }
          let variations = this.plans[i].variations
          let sizes = this.planSizeOptions(this.plans[i], variations)
          this.plans[i].sizeOptions = JSON.parse(JSON.stringify(sizes))
          let variation = this.plans[i]['variations'].reduce((prev, curr) => {
            let prevPrice = prev.price
            let currPrice = curr.price
            if (prev.paymentPeriodUnit === 0) {
              if (prev.paymentPeriodValue > 1) {
                prevPrice = parseFloat(prev.price) / parseFloat(prev.paymentPeriodValue)
              } else {
                prevPrice = parseFloat(prev.price)
              }
            } else if (prev.paymentPeriodUnit === 1) {
              if (prev.paymentPeriodValue > 1) {
                prevPrice = parseFloat(prev.price) / parseFloat(prev.paymentPeriodValue) / 12
              } else {
                prevPrice = parseFloat(prev.price) / 12
              }
            }
            if (curr.paymentPeriodUnit === 0) {
              if (curr.paymentPeriodValue > 1) {
                currPrice = parseFloat(curr.price) / parseFloat(curr.paymentPeriodValue)
              } else {
                currPrice = parseFloat(curr.price)
              }
            } else if (curr.paymentPeriodUnit === 1) {
              if (curr.paymentPeriodValue > 1) {
                currPrice = parseFloat(curr.price) / parseFloat(curr.paymentPeriodValue) / 12
              } else {
                currPrice = parseFloat(curr.price) / 12
              }
            }
            return prevPrice < currPrice ? prev : curr
          })
          if (this.currentVariation) {
            let selectedVariation = this.plans[i]['variations'].find(v => v.id === this.currentVariation.id)
            if (selectedVariation) {
              this.plans[i].selectedVariation = {
                minSize: selectedVariation.minSize,
                paymentPeriodUnit: selectedVariation.paymentPeriodUnit,
                paymentPeriodValue: selectedVariation.paymentPeriodValue,
                commitment: selectedVariation.commitment,
                selectedSavedVariation: true
              }
            }
          }
          if (!this.plans[i].selectedVariation) {
            this.plans[i].selectedVariation = {
              minSize: this.preSelectedSize ? this.preSelectedSize.min : null,
              paymentPeriodUnit: null,
              paymentPeriodValue: null,
              commitment: null
            }
          }
          this.plans[i].lowerPriceVariation = JSON.parse(JSON.stringify(variation)) || null
          if (!this.preSelectedSize && !!this.plans[i].lowerPriceVariation) {
            this.plans[i].selectedVariation.minSize = this.plans[i].lowerPriceVariation.minSize
          }
          this.plans[i].selectedPlanSize = this.selectedSizeOption(this.plans[i])
          this.plans[i].selectedPlanBilling = this.selectedBillingOption(this.plans[i])
          this.plans[i].selectedPlanCommitment = this.selectedCommitmentOption(this.plans[i])
          if (!this.plans[i].hasOwnProperty('variations') || !Array.isArray(this.plans[i].variations) || !this.plans[i].variations.length) {
            this.plans[i].sizeOptions = []
            this.plans[i].billingOptions = []
            this.plans[i].commitmentOptions = []
          }
          if (!this.plans[i].selectedVariation) {
            let billings = this.planBillingOptions(this.plans[i], variations)
            this.plans[i].billingOptions = JSON.parse(JSON.stringify(billings))
            let commitments = this.planCommitmentOptions(this.plans[i], variations)
            this.plans[i].commitmentOptions = JSON.parse(JSON.stringify(commitments))
          }
          this.filterOptions(this.plans[i])
          this.$nextTick(() => {
            delete this.plans[i].selectedVariation.selectedSavedVariation
          })
        }
      },
      getPlans () {
        let url = api.URLS.plans
        if (this.voucher) {
          url += `?voucher=${this.voucher}`
        }
        this.$http.get(url, api.headersAuthSettings).then((res) => {
          this.plans = Array.isArray(res.body) ? res.body.sort((prev, next) => prev.name.localeCompare(next.name)) : []
          this.findSelectedPlan()
          this.updatePlansVariation()
          this.$emit('plans-loaded', this.plans)
        }).catch((error) => {
          console.log(error)
        })
      },
      submitVoucher () {
        let data = {
          voucher: this.voucher
        }
        this.$http.post(api.URLS.voucherCheck, data, api.headersAuthSettings).then((res) => {
          this.invalidVoucher = !res.body.valid
          this.voucherSubmitted = true
          if (this.invalidVoucher === false) {
            sessionStorage.setItem('voucher', this.voucher.toString())
            this.getPlans()
          } else {
            this.voucher = ''
          }
        }).catch(() => {
          this.invalidVoucher = true
          this.voucher = ''
        })
      },
      submitAnotherVoucher () {
        this.hideVoucherSubmitForm = false
      },
      deleteVoucher () {
        sessionStorage.removeItem('voucher')
        this.hideVoucherSubmitForm = false
        this.voucher = null
        this.getPlans()
      },
      tabSelected (event) {
        if (!this.plans || !this.plans.length) {
          return
        }
        let plan = this.plans.find(p => p.name.toLowerCase().replace(/ /g, '-') === event.id)
        if (plan) {
          this.selectedPlan = plan
        }
      },
      activateVoucherArea () {
        if (this.voucherAreaActive && !this.voucherAreaTransitioning) {
          this.voucherAreaActive = false
          this.voucherSubmitted = false
          this.invalidVoucher = false
        } else {
          this.voucherAreaTransitioning = true
          setTimeout(() => {
            this.voucherAreaActive = true
            this.voucherAreaTransitioning = false
          }, 500)
        }
      }
    },
    watch: {
      plans (value) {
        if (value) {
          if (!this.selectedPlan) {
            this.findSelectedPlan()
          }
        }
      },
      currentPlan (value) {
        if (value) {
          if (!this.selectedPlan) {
            this.findSelectedPlan()
          }
        }
      },
      companySizes (value) {
        if (value) {
          this.getPlans()
        }
      }
    },
    created () {
      this.voucher = sessionStorage.getItem('voucher')
      this.hideVoucherSubmitForm = !!this.voucher
      this.getCompanySizes()
      // this.getPlans()
    },
    components: {
      AppSelect
    }
  }
</script>

<style src="@/assets/scss/components/subscriptions/plans.scss" lang="scss" scoped></style>



// WEBPACK FOOTER //
// PlansList.vue?6c81e78a